import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { TrendingUp, Hash, Users, Flame } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import api from '@/api/client'

export default function Explore() {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<'trending' | 'users'>('trending')

  const { data: trending } = useQuery({
    queryKey: ['trending-tags'],
    queryFn: () => api.get('/search/trending').then(r => r.data)
  })

  const { data: activeUsers } = useQuery({
    queryKey: ['active-users'],
    queryFn: () => api.get('/users/active').then(r => r.data)
  })

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <h1 className="font-display text-2xl font-bold mb-6">{t('nav.explore')}</h1>

      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('trending')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${
            activeTab === 'trending' ? 'bg-primary text-white' : 'bg-secondary text-muted-foreground'
          }`}
        >
          <TrendingUp className="w-4 h-4" /> {t('explore.trending')}
        </button>
        <button
          onClick={() => setActiveTab('users')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${
            activeTab === 'users' ? 'bg-primary text-white' : 'bg-secondary text-muted-foreground'
          }`}
        >
          <Users className="w-4 h-4" /> {t('explore.activeMembers')}
        </button>
      </div>

      {activeTab === 'trending' && (
        <div className="space-y-3">
          {trending?.map((tag: any, i: number) => (
            <Link key={tag._id} to={`/search?q=${encodeURIComponent(tag._id)}`}>
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-center gap-4 p-4 rounded-xl bg-card border border-border hover:border-primary/30 transition-colors"
              >
                <span className="text-2xl font-bold text-primary/30">{i + 1}</span>
                <Hash className="w-5 h-5 text-primary" />
                <div className="flex-1">
                  <p className="font-medium">{tag._id}</p>
                  <p className="text-xs text-muted-foreground">{tag.count} {t('explore.posts')}</p>
                </div>
                <Flame className="w-4 h-4 text-orange-500" />
              </motion.div>
            </Link>
          ))}
        </div>
      )}

      {activeTab === 'users' && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {activeUsers?.map((user: any) => (
            <Link key={user._id} to={`/profile/${user.username}`}>
              <div className="p-4 rounded-xl bg-card border border-border hover:border-primary/30 transition-colors text-center">
                <Avatar className="w-16 h-16 mx-auto mb-3">
                  <AvatarImage src={user.avatar} />
                  <AvatarFallback>{user.username[0]}</AvatarFallback>
                </Avatar>
                <p className="font-medium text-sm">{user.username}</p>
                <p className="text-xs text-muted-foreground">{user.contributionCount} {t('explore.contributions')}</p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
